import React, { useState } from 'react';
import { ValidationResults } from '../types';
import EnhancedValidationResultsDisplay from './EnhancedValidationResultsDisplay';

const ComprehensiveReview: React.FC = () => {
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [results, setResults] = useState<ValidationResults | null>(null);
  const [rawResponse, setRawResponse] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [subjectInfo, setSubjectInfo] = useState<any>(null);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [showActionModal, setShowActionModal] = useState(false);
  const [actionModalContent, setActionModalContent] = useState({ title: '', content: '' });
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState('');
  const [selectedSubject, setSelectedSubject] = useState<string>('');

  const sampleFiles = [
    { filename: 'subject1.json', subjectId: 'S001', name: 'Michael Thompson' },
    { filename: 'subject02.json', subjectId: 'S002', name: 'Jennifer Lynn Martinez' },
    { filename: 'subject3.json', subjectId: 'S003', name: 'Robert Wilson' },
    { filename: 'subject04.json', subjectId: 'S004', name: 'Sarah Katherine Johnson' },
    { filename: 'subject5.json', subjectId: 'S005', name: 'Darius Terrell Jackson' },
    { filename: 'subject6.json', subjectId: 'S006', name: 'David Chen' },
    { filename: 'subject7.json', subjectId: 'S007', name: 'Tyler Brooks' },
    { filename: 'subject8.json', subjectId: 'S008', name: 'Emily Samantha Patel' },
    { filename: 'subject9.json', subjectId: 'S009', name: 'Amanda Jane Nguyen' },
    { filename: 'subject10.json', subjectId: 'S010', name: 'Thomas William Reynolds' },
    { filename: 'subject11.json', subjectId: 'S011', name: 'Marcus Devin Bradley' },
    { filename: 'subject12.json', subjectId: 'S012', name: 'Richard James Blackwell' },
    { filename: 'subject13.json', subjectId: 'S013', name: 'Hiroshi Yamamoto' },
    { filename: 'subject17.json', subjectId: 'S017', name: 'Darnell Tyrone Washington' },
    { filename: 'subject20.json', subjectId: 'S020', name: 'Kevin Michael Rodriguez' }
  ];

  const progressSteps = [
    { message: 'Loading PVQ sample data...', duration: 1000 },
    { message: 'Extracting subject information...', duration: 800 },
    { message: 'Initializing AI validation system...', duration: 1200 },
    { message: 'Analyzing employment history timeline...', duration: 1500 },
    { message: 'Cross-referencing military service dates...', duration: 1300 },
    { message: 'Validating residence history coverage...', duration: 1100 },
    { message: 'Checking verifier relationships...', duration: 1400 },
    { message: 'Reviewing citizenship consistency...', duration: 1000 },
    { message: 'Identifying potential rejection patterns...', duration: 1600 },
    { message: 'Calculating confidence scores...', duration: 900 },
    { message: 'Generating recommendations...', duration: 1200 },
    { message: 'Finalizing validation results...', duration: 800 }
  ];

  const simulateProgress = () => {
    setProgress(0);
    setProgressMessage('');
    
    let currentStep = 0;
    let currentProgress = 0;
    
    const runStep = () => {
      if (currentStep < progressSteps.length) {
        const step = progressSteps[currentStep];
        setProgressMessage(step.message);
        
        // Animate progress for this step
        const targetProgress = ((currentStep + 1) / progressSteps.length) * 100;
        const progressIncrement = (targetProgress - currentProgress) / (step.duration / 50);
        
        const progressInterval = setInterval(() => {
          currentProgress += progressIncrement;
          if (currentProgress >= targetProgress) {
            currentProgress = targetProgress;
            clearInterval(progressInterval);
            
            // Move to next step after a brief pause
            setTimeout(() => {
              currentStep++;
              runStep();
            }, 100);
          }
          setProgress(Math.min(currentProgress, 100));
        }, 50);
      }
    };
    
    runStep();
  };

  const runComprehensiveReview = async () => {
    if (!selectedSubject) {
      setError('Please select a subject to analyze');
      return;
    }

    setIsAnalyzing(true);
    setError(null);
    setResults(null);
    setRawResponse(null);
    setSubjectInfo(null);

    // Start progress simulation
    simulateProgress();

    try {
      // Find the selected subject file
      const selectedFile = sampleFiles.find(file => file.filename === selectedSubject);
      if (!selectedFile) {
        throw new Error(`Invalid subject selection: ${selectedSubject}`);
      }

      // Fetch the sample data
      const sampleResponse = await fetch(`/pvq_samples/${selectedFile.filename}`);
      if (!sampleResponse.ok) {
        throw new Error(`Failed to load sample file: ${selectedFile.filename}`);
      }
      const sampleData = await sampleResponse.json();
      
      // Extract subject information
      const personalId = sampleData.partA?.personalIdentification;
      const fullName = personalId?.fullName 
        ? `${personalId.fullName.firstName} ${personalId.fullName.middleName ? personalId.fullName.middleName + ' ' : ''}${personalId.fullName.lastName}`
        : 'Unknown';
      const citizenship = personalId?.citizenship?.usCitizen ? 'United States' : 'Other';
      const placeOfBirth = personalId?.placeOfBirth 
        ? `${personalId.placeOfBirth.city}, ${personalId.placeOfBirth.state}`
        : 'Unknown';
      
      setSubjectInfo({
        name: fullName,
        subject_id: sampleData.formMetadata?.formId || 'Unknown',
        citizenship: citizenship,
        place_of_birth: placeOfBirth
      });

      // Call the lambda endpoint (same as PVQ Validator)
      const apiEndpoint = process.env.REACT_APP_API_ENDPOINT || 'https://625x3rqvui.execute-api.us-east-1.amazonaws.com/prod/validate';
      
      console.log('Running Comprehensive Review on:', selectedFile.filename);
      console.log('Calling API Gateway:', apiEndpoint);

      const response = await fetch(apiEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ pvq_data: sampleData })
      });

      console.log('API Gateway response status:', response.status);

      if (!response.ok) {
        throw new Error(`API request failed with status: ${response.status}`);
      }

      const responseData = await response.json();
      console.log('API Gateway response:', responseData);

      // Ensure progress reaches 100% before showing results
      setProgress(100);
      setProgressMessage('Analysis complete!');
      
      // Brief pause to show 100% completion
      await new Promise(resolve => setTimeout(resolve, 500));

      // Store raw response for display
      setRawResponse(responseData);

      if (responseData.success) {
        setResults(responseData.validation_results);
      } else {
        setError(responseData.error || 'Analysis failed');
      }
    } catch (err: any) {
      console.error('Comprehensive Review error:', err);
      setError(`Analysis error: ${err.message || 'Unknown error occurred'}`);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleSubmitPVQ = () => {
    setShowSubmitModal(true);
  };

  const handleConfirmSubmit = () => {
    setShowSubmitModal(false);
    // Reset the tool after submission
    setResults(null);
    setRawResponse(null);
    setError(null);
    setSubjectInfo(null);
    setProgress(0);
    setProgressMessage('');
    setSelectedSubject('');
  };

  const handleActionClick = (title: string, content: string) => {
    setActionModalContent({ title, content });
    setShowActionModal(true);
  };

  return (
    <div className="flex-1 p-8 bg-gray-50">
      <div className="max-w-6xl mx-auto">
        <div className="bg-white rounded-lg shadow-sm p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="flex justify-center mb-4">
              <div className="flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                </svg>
              </div>
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Comprehensive Final Review</h2>
            <p className="text-lg text-gray-600 mb-8">
              AI-powered analysis of complete PVQ submissions using live validation data
            </p>
          </div>

          {/* Feature Overview and Demo Action - Hidden when results are shown */}
          {!results && !error && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-xl p-6">
                  <div className="flex items-center mb-4">
                    <div className="flex items-center justify-center w-10 h-10 bg-blue-500 rounded-full mr-3">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-blue-900">Live Data Analysis</h3>
                  </div>
                  <p className="text-blue-800 text-sm">
                    Select from real PVQ sample files and process them through the live lambda endpoint
                  </p>
                </div>

                <div className="bg-gradient-to-br from-purple-50 to-purple-100 border border-purple-200 rounded-xl p-6">
                  <div className="flex items-center mb-4">
                    <div className="flex items-center justify-center w-10 h-10 bg-purple-500 rounded-full mr-3">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-purple-900">AI-Powered Insights</h3>
                  </div>
                  <p className="text-purple-800 text-sm">
                    Comprehensive validation results with critical issues, warnings, and intelligent recommendations
                  </p>
                </div>
              </div>

              {/* Subject Selection and Demo Action */}
              <div className="text-center mb-8 space-y-4">
                <div className="max-w-md mx-auto">
                  <label htmlFor="subject-select" className="block text-sm font-medium text-gray-700 mb-2">
                    Select Subject to Analyze
                  </label>
                  <select
                    id="subject-select"
                    value={selectedSubject}
                    onChange={(e) => setSelectedSubject(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                  >
                    <option value="">Choose a subject...</option>
                    {sampleFiles.map((file) => (
                      <option key={file.filename} value={file.filename}>
                        Subject {file.subjectId} - {file.name}
                      </option>
                    ))}
                  </select>
                </div>

                <button
                  onClick={runComprehensiveReview}
                  disabled={isAnalyzing || !selectedSubject}
                  className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white text-lg font-semibold rounded-xl hover:from-blue-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl"
                >
                  {isAnalyzing ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-6 w-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Analyzing PVQ Data...
                    </>
                  ) : (
                    <>
                      <svg className="w-6 h-6 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                      </svg>
                      Run Comprehensive Review
                    </>
                  )}
                </button>
              </div>
            </>
          )}

          {/* Subject Information Display */}
          {subjectInfo && (
            <div className="mb-6">
              <div className="bg-gradient-to-r from-gray-50 to-blue-50 border border-gray-200 rounded-lg p-6">
                <div className="flex items-start">
                  <div className="flex items-center justify-center w-10 h-10 bg-blue-100 rounded-full mr-4 mt-1">
                    <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      Subject Analyzed: {subjectInfo.name}
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                      <div className="bg-white rounded-md px-3 py-2 border border-gray-200">
                        <span className="text-gray-500 block">Subject ID</span>
                        <span className="font-medium text-gray-900">{subjectInfo.subject_id}</span>
                      </div>
                      <div className="bg-white rounded-md px-3 py-2 border border-gray-200">
                        <span className="text-gray-500 block">Citizenship</span>
                        <span className="font-medium text-gray-900">{subjectInfo.citizenship}</span>
                      </div>
                      <div className="bg-white rounded-md px-3 py-2 border border-gray-200">
                        <span className="text-gray-500 block">Place of Birth</span>
                        <span className="font-medium text-gray-900">{subjectInfo.place_of_birth}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Progress Bar */}
          {isAnalyzing && (
            <div className="mb-6">
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <div className="flex items-center mb-4">
                  <div className="flex items-center justify-center w-10 h-10 bg-blue-100 rounded-full mr-4">
                    <svg className="w-5 h-5 text-blue-600 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">AI Analysis in Progress</h3>
                    <p className="text-sm text-gray-600">{progressMessage}</p>
                  </div>
                </div>
                
                <div className="mb-3">
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-sm font-medium text-gray-700">Analysis Progress</span>
                    <span className="text-sm font-medium text-blue-600">{Math.round(progress)}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-gradient-to-r from-blue-500 to-purple-500 h-2 rounded-full transition-all duration-300 ease-out"
                      style={{ width: `${progress}%` }}
                    ></div>
                  </div>
                </div>
                
                <div className="text-xs text-gray-500 text-center">
                  Processing through AWS Lambda with AI-powered validation
                </div>
              </div>
            </div>
          )}

          {/* Error Display */}
          {error && (
            <div className="mb-6">
              <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                <div className="flex items-center mb-2">
                  <svg className="w-5 h-5 text-red-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.728-.833-2.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                  <h3 className="text-lg font-medium text-red-900">Analysis Error</h3>
                </div>
                <p className="text-red-700">{error}</p>
              </div>
            </div>
          )}

          {/* Results Display */}
          {results && (
            <>
              <EnhancedValidationResultsDisplay 
                results={results} 
                rawResponse={rawResponse} 
                onActionClick={handleActionClick}
              />
              
              {/* Submit PVQ Button */}
              <div className="mt-8 text-center">
                <button
                  onClick={handleSubmitPVQ}
                  className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-green-600 to-blue-600 text-white text-lg font-semibold rounded-xl hover:from-green-700 hover:to-blue-700 transition-all duration-200 shadow-lg hover:shadow-xl"
                >
                  <svg className="w-6 h-6 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Submit PVQ for FSO Review
                </button>
              </div>
            </>
          )}
          
          {/* Placeholder State */}
          {!isAnalyzing && !error && !results && (
            <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-xl p-12 text-center">
              <div className="text-gray-400">
                <svg className="mx-auto h-12 w-12 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p className="text-lg font-medium text-gray-900">Ready for Comprehensive Review</p>
                <p className="text-sm mt-2 text-gray-600">
                  Select a subject from the dropdown above to analyze their PVQ with live AI validation
                </p>
              </div>
            </div>
          )}

          {/* Information Panel - Hidden when results are shown */}
          {!results && !error && (
            <div className="mt-8 p-6 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl">
              <div className="flex items-start">
                <div className="flex items-center justify-center w-10 h-10 bg-blue-100 rounded-full mr-4 mt-1">
                  <svg className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-blue-900 mb-2">Live Demonstration</h4>
                  <p className="text-blue-800 text-sm mb-3">
                    This demonstration showcases the comprehensive review capability by:
                  </p>
                  <ul className="text-blue-700 text-sm space-y-1 list-disc list-inside">
                    <li>Choose from {sampleFiles.length} real PVQ sample files with named subjects</li>
                    <li>Processing the data through the same AWS Lambda endpoint used by the PVQ Validator</li>
                    <li>Displaying comprehensive validation results with AI-powered insights</li>
                    <li>Showing critical issues, warnings, and recommendations for improvement</li>
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Submit PVQ Confirmation Modal */}
      {showSubmitModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-8 max-w-md w-full mx-4">
            <div className="flex items-center mb-6">
              <div className="flex items-center justify-center w-12 h-12 bg-green-100 rounded-full mr-4">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900">Submit PVQ for Review</h3>
            </div>
            
            <div className="mb-6">
              <p className="text-gray-700 mb-4">
                You are about to submit this PVQ for FSO review. Based on the analysis, please consider addressing the identified issues to reduce the likelihood of application rejection or delays.
              </p>
              
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-start">
                  <svg className="w-5 h-5 text-amber-600 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.728-.833-2.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                  <div className="text-sm">
                    <p className="font-medium text-amber-900">Recommendation:</p>
                    <p className="text-amber-800 mt-1">
                      Review critical issues and warnings identified in the analysis before submission to improve approval chances.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowSubmitModal(false)}
                className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmSubmit}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                Submit PVQ
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Action Modal */}
      {showActionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-8 max-w-md w-full mx-4">
            <div className="flex items-center mb-6">
              <div className="flex items-center justify-center w-12 h-12 bg-blue-100 rounded-full mr-4">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900">{actionModalContent.title}</h3>
            </div>
            
            <div className="mb-6">
              <p className="text-gray-700">
                {actionModalContent.content}
              </p>
            </div>

            <div className="flex justify-end">
              <button
                onClick={() => setShowActionModal(false)}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ComprehensiveReview;